import React from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/shared";
import { NewsletterInline } from "@/components/Newsletter";
import { ArrowUpRight } from "lucide-react";

function Rail({ n, label }) {
  return (
    <div className="mb-4 lg:mb-0 lg:w-[140px] lg:flex-shrink-0">
      <span className="kicker text-xs text-brass">{n}</span>
      <span className="kicker ml-3 text-xs text-mutedink lg:ml-0 lg:mt-1 lg:block">{label}</span>
    </div>
  );
}

const GROUPS = [
  {
    n: "01",
    label: "AI toolkit",
    heading: "The AI toolkit",
    intro: "The exact prompts, agents and workflows I run every morning — packaged so you can drop them straight into your own business.",
    items: [
      ["The morning P&L agent", "Reads your numbers and hands you a prioritised action queue before 9am."],
      ["Daily content engine", "Researches, writes and schedules — one real story a day, on its own."],
      ["Follow-up autopilot", "The system that remembers every lead and customer so you don't have to."],
    ],
  },
  {
    n: "02",
    label: "Checklists",
    heading: "Operating checklists",
    intro: "One-page checklists for the decisions that keep coming back — pricing, hiring, delegation, cash.",
    items: [
      ["The price-increase checklist", "How to raise prices without losing the customers who matter."],
      ["The delegation checklist", "What to hand off first, and how to make sure it actually gets done."],
      ["The weekly numbers review", "The five numbers to look at every Monday, and what to do about each."],
    ],
  },
  {
    n: "03",
    label: "Prompt packs",
    heading: "Prompt packs",
    intro: "Copy-paste prompt libraries for sales, marketing and operations — written for a product business, not a demo.",
    items: [
      ["Sales & objections", "Prompts that draft replies, handle objections and follow up in your voice."],
      ["Marketing & content", "Turn one idea into a week of posts, emails and scripts."],
      ["Operations & SOPs", "Turn a messy process in your head into a document your team can follow."],
    ],
  },
];

function ItemCard({ title, desc }) {
  return (
    <div className="flex flex-col border border-rule bg-card p-5 rounded-[3px]" data-testid={`resource-item-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-heading text-base font-semibold text-ink">{title}</h3>
        <span className="kicker flex-shrink-0 rounded-[3px] border border-brass/40 px-2 py-0.5 text-[10px] text-brass">Coming soon</span>
      </div>
      <p className="mt-2 text-sm text-bodytext">{desc}</p>
    </div>
  );
}

export default function Resources() {
  return (
    <PublicShell>
      {/* Hero */}
      <section className="border-b border-rule">
        <div className="mx-auto max-w-[1040px] px-5 py-20 lg:px-8">
          <p className="kicker text-xs text-brass" data-testid="resources-kicker">Resources</p>
          <h1 className="mt-5 font-heading text-4xl font-bold leading-[1.1] text-ink sm:text-5xl" data-testid="resources-title">
            The tools I actually use to sell more, grow smarter and run on AI.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-bodytext">
            A growing library of AI workflows, one-page checklists and prompt packs — everything drawn from businesses I run myself. New pieces land here regularly.
          </p>
        </div>
      </section>

      {/* Groups */}
      {GROUPS.map((g) => (
        <section key={g.n} className="border-b border-rule">
          <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
            <Rail n={g.n} label={g.label} />
            <div className="flex-1">
              <h2 className="font-heading text-2xl font-bold text-ink">{g.heading}</h2>
              <p className="mt-3 max-w-2xl text-bodytext">{g.intro}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                {g.items.map(([t, d]) => <ItemCard key={t} title={t} desc={d} />)}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* The Stack cross-link */}
      <section className="border-b border-rule">
        <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
          <Rail n="04" label="The stack" />
          <div className="flex-1">
            <h2 className="font-heading text-2xl font-bold text-ink">The software behind all of it</h2>
            <p className="mt-3 max-w-2xl text-bodytext">
              The actual tools I run a product business on in 2026 — and what each one replaced. Honest notes, no fluff.
            </p>
            <Link to="/stack" data-testid="resources-stack-link" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline">
              See The New Stack <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section>
        <div className="mx-auto flex max-w-[1040px] flex-col px-5 py-16 lg:flex-row lg:px-8">
          <Rail n="05" label="Stay close" />
          <div className="flex-1">
            <h2 className="font-heading text-2xl font-bold text-ink">Get new resources as they drop</h2>
            <p className="mt-3 max-w-2xl text-bodytext">Subscribe to the daily and you'll be first to know when a new toolkit, checklist or prompt pack goes live.</p>
            <div className="mt-6 max-w-xl rounded-[3px] border border-rule bg-card p-6" data-testid="resources-newsletter">
              <NewsletterInline source="resources" compact />
              <p className="mt-2 text-xs text-mutedink">Free. One email a day. Unsubscribe in one click.</p>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
